import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "@/lib/api";
import { bootProfilePrefs } from "@/lib/profilePrefs";
import {
  maskName as maskNameRaw,
  maskEmail as maskEmailRaw,
  maskPhone as maskPhoneRaw,
  maskAddress as maskAddressRaw,
} from "@/lib/format";

/**
 * "Tutorial mode": a per-account recording-safety switch. When on, every console
 * (seller dashboard, operator, reseller) masks buyer & account PII — names,
 * emails, phones, addresses — plus product titles and store names, so a
 * walkthrough can be screen-recorded, or live-sales proof shared, without
 * exposing customer data or which package is selling. Everything is starred the
 * same way names are, so a board still reads as a real board. It only changes
 * how things render — no stored data is touched, and CSV exports deliberately
 * bypass masking (a downloaded file isn't on camera; a starred export is just
 * broken data).
 *
 * Product titles are masked in every list, table and detail view, but NOT in the
 * product editor's own form fields — masking there would make "how to create a
 * product" impossible to record, which is the whole point of the mode.
 *
 * Mirrors the LanguageProvider pattern: a synchronous localStorage read seeds the
 * first paint (a refresh never flips the choice), and the account value from
 * `/profile` is only adopted on a fresh device where no local choice exists yet.
 * A toggle writes both localStorage and the profile so the choice follows the
 * account across devices.
 */

const STORAGE_KEY = "tutorial_mode";

function readStored(): boolean | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "1" ? true : v === "0" ? false : null;
  } catch {
    return null;
  }
}

type Maskable = string | null | undefined;

/**
 * A masker keeps the caller's own nullability: handed a `string` it returns a
 * `string`, so a masked value drops straight into a `name: string` prop with no
 * `?? original` fallback — and a fallback is exactly how a leak sneaks back in.
 */
type Masker = <T extends Maskable>(value: T) => T extends string ? string : T;

interface TutorialModeValue {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  toggle: () => void;
  /** Masks a name when tutorial mode is on; returns it unchanged otherwise. */
  maskName: Masker;
  /** Masks an email when tutorial mode is on; returns it unchanged otherwise. */
  maskEmail: Masker;
  /** Masks a product title when tutorial mode is on; returns it unchanged otherwise. */
  maskProduct: Masker;
  /** Masks a store/site name — on this platform that IS the seller's personal name. */
  maskStore: Masker;
  /** Masks a phone number when tutorial mode is on; returns it unchanged otherwise. */
  maskPhone: Masker;
  /** Masks a street address when tutorial mode is on; returns it unchanged otherwise. */
  maskAddress: Masker;
}

const TutorialModeContext = createContext<TutorialModeValue | null>(null);

export function TutorialModeProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(() => readStored() ?? false);
  const hasLocalChoice = useRef(readStored() !== null);

  useEffect(() => {
    if (hasLocalChoice.current) return;
    let active = true;
    bootProfilePrefs()
      .then((r) => {
        if (active && !hasLocalChoice.current && typeof r.tutorialMode === "boolean") {
          setEnabledState(r.tutorialMode);
        }
      })
      .catch(() => {
        /* signed out / no profile — keep the default */
      });
    return () => {
      active = false;
    };
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next);
    hasLocalChoice.current = true;
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* private mode — in-memory only */
    }
    // Fire-and-forget: a failed sync just leaves the account default unchanged;
    // localStorage already holds the live choice.
    api.patch("/profile", { tutorialMode: next }, { optionalAuth: true }).catch(() => {});
  }, []);

  const value = useMemo<TutorialModeValue>(() => {
    // Each raw masker is already null-preserving; `gate` only decides whether it
    // runs. The cast re-attaches the caller's own type, which the generic
    // signature promises but a runtime function can't prove.
    const gate =
      (fn: (v: Maskable) => Maskable): Masker =>
      (v) =>
        (enabled ? fn(v) : v) as never;
    return {
      enabled,
      setEnabled,
      toggle: () => setEnabled(!enabled),
      maskName: gate(maskNameRaw),
      maskEmail: gate(maskEmailRaw),
      maskProduct: gate(maskNameRaw),
      maskStore: gate(maskNameRaw),
      maskPhone: gate(maskPhoneRaw),
      maskAddress: gate(maskAddressRaw),
    };
  }, [enabled, setEnabled]);

  return (
    <TutorialModeContext.Provider value={value}>{children}</TutorialModeContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTutorialMode(): TutorialModeValue {
  const ctx = useContext(TutorialModeContext);
  if (!ctx) throw new Error("useTutorialMode must be used within TutorialModeProvider");
  return ctx;
}
